/**
 * Operational Monitoring Dashboard
 * 
 * Production monitoring for:
 * - System health
 * - Error tracking
 * - Payment audit
 * - Deployment readiness
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Status badge component
const StatusBadge = ({ status }) => {
  const colors = {
    healthy: 'bg-emerald-100 text-emerald-800',
    PASS: 'bg-emerald-100 text-emerald-800',
    CONFIGURED: 'bg-emerald-100 text-emerald-800',
    warning: 'bg-amber-100 text-amber-800',
    WARNING: 'bg-amber-100 text-amber-800',
    degraded: 'bg-amber-100 text-amber-800',
    unhealthy: 'bg-red-100 text-red-800',
    FAIL: 'bg-red-100 text-red-800',
    'MISSING (CRITICAL)': 'bg-red-100 text-red-800',
    'MISSING (recommended)': 'bg-amber-100 text-amber-800',
    'WEAK': 'bg-amber-100 text-amber-800',
    'WARNING (allow all)': 'bg-amber-100 text-amber-800',
  };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${colors[status] || 'bg-slate-100 text-slate-700'}`}>
      {status}
    </span>
  );
};

// Card component
const Card = ({ title, children, className = '' }) => (
  <div className={`bg-slate-800 rounded-xl border border-slate-700 overflow-hidden ${className}`}>
    {title && (
      <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
        <h3 className="font-semibold text-white">{title}</h3>
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

export default function OperationalMonitoringPage() {
  const [health, setHealth] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [errors, setErrors] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('health');
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Load health check
      const healthRes = await axios.get(`${API_URL}/api/monitoring/health/detailed`, { headers });
      setHealth(healthRes.data);
      
      // Load deployment readiness
      const readinessRes = await axios.get(`${API_URL}/api/monitoring/deployment-readiness`, { headers });
      setReadiness(readinessRes.data);
      
      // Load recent errors
      try {
        const errorsRes = await axios.get(`${API_URL}/api/monitoring/errors?limit=20`, { headers });
        setErrors(errorsRes.data.errors || []);
      } catch (e) {
        console.log('Error logs not available');
      }
      
      // Load payment stats
      try {
        const paymentRes = await axios.get(`${API_URL}/api/monitoring/payments/status`, { headers });
        setPaymentStats(paymentRes.data);
      } catch (e) {
        console.log('Payment stats not available');
      }
      
      setLastRefresh(new Date());
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load monitoring data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading && !health) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading monitoring data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="operational-monitoring">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Operational Monitoring</h1>
            <p className="text-slate-400 text-sm mt-1">
              Production health, errors, and deployment readiness
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">
              Last refresh: {lastRefresh?.toLocaleTimeString() || 'Never'}
            </span>
            <button
              onClick={loadData}
              disabled={loading}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </header>

      {/* Top-level Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="text-center">
          <div className="text-4xl font-bold text-white mb-1">
            {health?.status === 'healthy' ? '✓' : '!'}
          </div>
          <div className="text-sm text-slate-400">System Status</div>
          <StatusBadge status={health?.status || 'unknown'} />
        </Card>
        
        <Card className="text-center">
          <div className="text-4xl font-bold text-white mb-1">
            {readiness?.readiness_percentage || 0}%
          </div>
          <div className="text-sm text-slate-400">Production Ready</div>
          <StatusBadge status={readiness?.readiness_percentage >= 90 ? 'PASS' : 'WARNING'} />
        </Card>
        
        <Card className="text-center">
          <div className="text-4xl font-bold text-white mb-1">
            {errors.filter(e => e.severity === 'ERROR').length}
          </div>
          <div className="text-sm text-slate-400">Recent Errors</div>
          <StatusBadge status={errors.filter(e => e.severity === 'ERROR').length === 0 ? 'healthy' : 'warning'} />
        </Card>
        
        <Card className="text-center">
          <div className="text-4xl font-bold text-white mb-1">
            {paymentStats?.summary?.failed_needing_attention || 0}
          </div>
          <div className="text-sm text-slate-400">Failed Payments</div>
          <StatusBadge status={(paymentStats?.summary?.failed_needing_attention || 0) === 0 ? 'healthy' : 'warning'} />
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-700 pb-2">
        {['health', 'readiness', 'errors', 'payments'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab 
                ? 'bg-teal-600 text-white' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'health' && health && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="System Checks">
            <div className="space-y-3">
              {Object.entries(health.checks || {}).map(([key, check]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                  <span className="text-slate-300 capitalize">{key.replace(/_/g, ' ')}</span>
                  <StatusBadge status={typeof check === 'object' ? check.status : check} />
                </div>
              ))}
            </div>
          </Card>
          
          <Card title="Environment Info">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Environment</span>
                <span className="text-white font-mono">{health.environment}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Timestamp</span>
                <span className="text-white font-mono">{new Date(health.timestamp).toLocaleString()}</span>
              </div>
              {health.checks?.database?.collections && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Collections</span>
                  <span className="text-white font-mono">{health.checks.database.collections}</span>
                </div>
              )}
              {health.checks?.stripe && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Stripe Mode</span>
                  <span className={`font-mono ${health.checks.stripe.mode === 'live' ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {health.checks.stripe.mode}
                  </span>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'readiness' && readiness && (
        <div className="space-y-4">
          {/* Blockers */}
          {readiness.blockers?.length > 0 && (
            <Card title="🚫 Blockers (Must Fix)">
              <ul className="space-y-2">
                {readiness.blockers.map((blocker, i) => (
                  <li key={i} className="flex items-center gap-2 text-red-400">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    {blocker}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          
          {/* Warnings */}
          {readiness.warnings?.length > 0 && (
            <Card title="⚠️ Warnings (Recommended)">
              <ul className="space-y-2">
                {readiness.warnings.map((warning, i) => (
                  <li key={i} className="flex items-center gap-2 text-amber-400">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    {warning}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          
          {/* Checklist */}
          <Card title="Deployment Checklist">
            <div className="space-y-3">
              {Object.entries(readiness.checklist || {}).map(([key, check]) => (
                <div key={key} className="border-b border-slate-700 pb-3 last:border-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-slate-300 font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                    <StatusBadge status={typeof check === 'object' ? check.status : check} />
                  </div>
                  {typeof check === 'object' && Object.entries(check).map(([subKey, subVal]) => {
                    if (subKey === 'status') return null;
                    if (typeof subVal === 'object') return null;
                    return (
                      <div key={subKey} className="flex justify-between text-xs text-slate-500 ml-4">
                        <span>{subKey}</span>
                        <span>{String(subVal)}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'errors' && (
        <Card title={`Recent Errors (${errors.length})`}>
          {errors.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <p>No recent errors</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700">
                    <th className="text-left py-2 px-2">Severity</th>
                    <th className="text-left py-2 px-2">Type</th>
                    <th className="text-left py-2 px-2">Message</th>
                    <th className="text-left py-2 px-2">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((error, i) => (
                    <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                      <td className="py-2 px-2">
                        <StatusBadge status={error.severity} />
                      </td>
                      <td className="py-2 px-2 text-slate-300 font-mono text-xs">{error.error_type}</td>
                      <td className="py-2 px-2 text-slate-400 truncate max-w-xs">{error.message}</td>
                      <td className="py-2 px-2 text-slate-500 text-xs">
                        {new Date(error.timestamp).toLocaleTimeString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'payments' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="Payment Status (30 Days)">
            {paymentStats?.summary?.by_status ? (
              <div className="space-y-3">
                {Object.entries(paymentStats.summary.by_status).map(([status, data]) => (
                  <div key={status} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                    <div>
                      <span className="text-slate-300 capitalize">{status || 'Unknown'}</span>
                      <span className="text-slate-500 text-xs ml-2">({data.count} transactions)</span>
                    </div>
                    <span className="text-white font-mono">
                      £{(data.total || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">No payment data available</p>
            )}
          </Card>
          
          <Card title="Payment System Info">
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-slate-400">Stripe Mode</span>
                <span className={`font-mono ${paymentStats?.stripe_mode === 'live' ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {paymentStats?.stripe_mode || 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700">
                <span className="text-slate-400">Failed (Need Attention)</span>
                <span className="text-red-400 font-mono">
                  {paymentStats?.summary?.failed_needing_attention || 0}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-400">Pending (24h)</span>
                <span className="text-amber-400 font-mono">
                  {paymentStats?.summary?.pending_24h || 0}
                </span>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
